import startCase from "lodash.startcase";

interface BreadcrumbItem {
   text: string;
   name: string;
   params?: Record<string, unknown>;
   query?: Record<string, unknown>;
   disabled?: boolean;
}

// Static crumbs reused across multiple route configs.
const PD_INDEX_CRUMB: BreadcrumbItem = { text: "PD Course", name: "pd-index" };
const PROFILE_CLASSES_CRUMB: BreadcrumbItem = {
   text: "Profile",
   name: "profile-classes",
};
const PROFILE_CLASSES_LABEL_CRUMB: BreadcrumbItem = {
   text: "Classes",
   name: "profile-classes",
};
const HELP_CENTER_CRUMB: BreadcrumbItem = {
   text: "Help Center",
   name: "help-center-index",
};
const PROFILE_SETTINGS_CRUMB: BreadcrumbItem = {
   text: "Settings",
   name: "profile-settings-general",
   disabled: true,
};

export function useBreadcrumbs() {
   const route = useRoute();

   const breadcrumbItems = useState<BreadcrumbItem[] | null>(
      "breadcrumbItems",
      () => null
   );
   const pageTitle = useState<string | null>("pageTitle", () => null);
   const resourceType = useState<string | null>("resourceType", () => null);
   // Real titles for intermediate crumbs, keyed by crumb (e.g. "coreWork").
   // Crumb factories prefer these over startCase(slug); see getCoreWork etc.
   const crumbTitles = useState<Record<string, string>>(
      "crumbTitles",
      () => ({})
   );

   const setPageTitle = (title: string) => {
      pageTitle.value = title;
   };
   const setResourceType = (title: string) => {
      resourceType.value = title;
   };
   const setCrumbTitle = (key: string, title: string) => {
      crumbTitles.value = { ...crumbTitles.value, [key]: title };
   };

   // >> COMPUTED CRUMB FACTORIES (reactive on route params)
   const getDiscipline = computed<BreadcrumbItem>(() => ({
      text: startCase(String(route.params?.discipline ?? "")),
      name: "discipline-explore",
      params: { discipline: route.params?.discipline },
   }));

   const getPage = computed<BreadcrumbItem>(() => ({
      text: pageTitle.value as string,
      name: route.name as string,
      disabled: true,
   }));

   const getResourceType = computed<BreadcrumbItem>(() => ({
      text: convertType(resourceType?.value ?? "", String(route.params.discipline ?? "")) ?? "",
      name: "discipline-resources",
      params: { discipline: route.params.discipline },
      query: { teachable_type: resourceType.value },
   }));

   const getUnit = computed<BreadcrumbItem>(() => ({
      text:
         crumbTitles.value.unit ??
         startCase((route.params?.planningTool as string).replace(/-/g, " ")),
      name: "unit",
      params: {
         discipline: route.params.discipline,
         slug: route.params.planningTool,
      },
   }));

   const getPlanningTools = computed<BreadcrumbItem>(() => ({
      text: "Unit & Sequences",
      name: "discipline-planning-tools",
      params: { discipline: route.params.discipline },
   }));

   const getCoreCategories = computed<BreadcrumbItem>(() => ({
      text: "Core Works",
      name: "discipline-core-categories",
      params: { discipline: route.params.discipline },
   }));

   const getCoreWork = computed<BreadcrumbItem>(() => ({
      text:
         crumbTitles.value.coreWork ??
         startCase((route.params?.corework as string).replace(/-/g, " ")),
      name: "core-work",
      params: {
         discipline: route.params.discipline,
         slug: route.params?.corework,
      },
   }));

   const getPDCourse = computed<BreadcrumbItem>(() => ({
      text:
         crumbTitles.value.pdCourse ??
         startCase((route.params.course as string).replace(/-/g, " ")),
      name: "pd-course",
      params: { course: route.params?.course },
   }));

   // >> ROUTE → CRUMB-LIST CONFIG
   const profileSettingsCrumbs = (): BreadcrumbItem[] => [
      PROFILE_CLASSES_CRUMB,
      PROFILE_SETTINGS_CRUMB,
   ];

   const breadcrumbsByRoute: Record<string, () => BreadcrumbItem[]> = {
      teachable: () => [
         getDiscipline.value,
         {
            text: "Teachable",
            name: "discipline-resources",
            params: { discipline: route.params.discipline },
         },
         getResourceType.value,
         getPage.value,
      ],
      "planning-tool-teachable": () => [
         getDiscipline.value,
         getPlanningTools.value,
         getUnit.value,
         getPage.value,
      ],
      "core-work-teachable": () => [
         getDiscipline.value,
         getCoreCategories.value,
         getCoreWork.value,
         getPage.value,
      ],
      "core-work": () => [
         getDiscipline.value,
         getCoreCategories.value,
         getPage.value,
      ],
      unit: () => [
         getDiscipline.value,
         getPlanningTools.value,
         getPage.value,
      ],
      "artistic-insight": () => [
         getDiscipline.value,
         getCoreCategories.value,
         getCoreWork.value,
         getPage.value,
      ],
      "pd-course": () => [PD_INDEX_CRUMB, getPDCourse.value],
      "pd-section": () => [PD_INDEX_CRUMB, getPDCourse.value, getPage.value],
      class: () => [
         PROFILE_CLASSES_CRUMB,
         PROFILE_CLASSES_LABEL_CRUMB,
         getPage.value,
      ],
      "help-center-topic": () => [HELP_CENTER_CRUMB, getPage.value],
      "profile-settings-general": profileSettingsCrumbs,
      "profile-settings-security": profileSettingsCrumbs,
   };

   const updateBreadcrumbs = () => {
      const builder = breadcrumbsByRoute[route.name as string];
      breadcrumbItems.value = builder ? builder() : [];
   };

   return {
      setPageTitle,
      setResourceType,
      setCrumbTitle,
      breadcrumbItems,
      pageTitle,
      updateBreadcrumbs,
   };
}
